import type {ExperimentStore} from '@github-ui/experiments/internal'
import {ExperimentScopeProvider} from '@github-ui/experiments/internal/ExperimentProvider'
import {useNavigationScopeTransaction} from '@github-ui/experiments/internal/navigation'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import {
  createTanStackRouter,
  Outlet,
  type RouteObject,
  type TanStackRouterHistory,
  useNavigation,
  useRenderedLocation,
} from '@github-ui/react-router'
import {IS_SERVER} from '@github-ui/ssr-utils'
import type {ComponentType, PropsWithChildren, ReactNode} from 'react'

import {BaseProviders} from '../BaseProviders'
import {CommonElements} from '../CommonElements'
import {RouterDevTools} from '../RouterDevTools'
import {RoutesContextProvider} from '../RoutesContextProvider'
import {AppPayloadContext} from '../use-app-payload'
import {NavigationFocusListener} from './NavigationFocusListener'
import {PublishPayload} from './PublishPayload'
import {getRouteOptionsForQueryRoute} from './query-route-tanstack-adapters'
import {
  RootAppRouteErrorElement,
  RootAppRouteErrorInner,
  RootNotFoundComponent,
  UnhandledRouteError,
  UnhandledRouteErrorInner,
} from './RouterErrorBoundary'
import {CombinedScrollRestoration} from './ScrollRestoration'
import {SoftNavLifecycleListener} from './SoftNavLifecycleListener'
import {TANSTACK_ROUTER_OPTIONS} from './tanstack-router-options'
import {TitleManager} from './TitleManager'

export function routesWithProviders(
  routes: RouteObject[],
  options: Omit<SharedProviderOptions, 'tanstackRouterEnabled'> & {
    HydrateFallback?: ComponentType
  },
): RouteObject[] {
  const {HydrateFallback, ...rest} = options
  return [
    {
      id: `__DATA_ROUTER_ROOT__`,
      errorElement: <UnhandledRouteError appName={rest.appName} />,
      HydrateFallback,
      element: <SharedProviders routes={routes} options={rest} />,
      children: [
        {
          id: `__DATA_ROUTER_APPLICATION_ROUTES__`,
          errorElement: <RootAppRouteErrorElement appName={rest.appName} />,
          children: routes,
        },
      ],
    },
  ]
}

export function tanStackRouterWithProviders<TContext extends {}>(
  routes: RouteObject[],
  options: Omit<SharedProviderOptions, 'tanstackRouterEnabled' | 'dataRouterEnabled'> & {
    context: TContext
    history?: TanStackRouterHistory
    HydrateFallback?: ComponentType
  },
) {
  const {history, context, HydrateFallback, ...rest} = options
  const rootOptions = {...rest, dataRouterEnabled: true, tanstackRouterEnabled: true}

  const RootComponent = () => <SharedProviders routes={routes} options={rootOptions} />

  const RootErrorComponent = ({error}: {error: unknown}) => (
    <UnhandledRouteErrorInner routeError={error} appName={rest.appName} />
  )

  const AppRoutesErrorComponent = ({error}: {error: unknown}) => (
    <RootAppRouteErrorInner routeError={error} appName={rest.appName} />
  )

  return createTanStackRouter({
    ...TANSTACK_ROUTER_OPTIONS,
    context,
    rootComponent: RootComponent,
    rootPendingComponent: HydrateFallback ? () => <HydrateFallback /> : undefined,
    rootErrorComponent: RootErrorComponent,
    appErrorComponent: AppRoutesErrorComponent,
    // Equivalent to isNoRouteFoundError branch of UnhandledRouteErrorInner in React Router context.
    // In TSR, not found responses are handled separately from regular errors.
    defaultNotFoundComponent: RootNotFoundComponent,
    routeObjects: routes,
    history,
    // Errors are already reported by error components.
    // Providing a defaultOnCatch suppresses duplicate reporting in createOnCaughtError.
    defaultOnCatch: noop,
    getRouteOptions: getRouteOptionsForQueryRoute,
    // Disable scroll restoration script during SSR where CSP blocks it
    scrollRestoration: !IS_SERVER,
  })
}

type SharedProviderOptions = {
  appPayload?: Record<string, unknown>
  appName: string
  ssrError: HTMLScriptElement | undefined
  dataRouterEnabled: boolean
  tanstackRouterEnabled?: boolean
  children?: ReactNode
  ssrExperimentStore?: ExperimentStore
}

/**
 * Shared provider tree used by both `routesWithProviders` (React Router) and
 * `tanStackRouterWithProviders` (TanStack Router).
 *
 * When `tanstackRouterEnabled` is true, `RoutesContextProvider` is omitted
 * (TSR manages its own route context) and hooks that lack TSR shims are suppressed.
 */
function SharedProviders({
  routes,
  options: {appPayload, appName, ssrError, children, dataRouterEnabled, tanstackRouterEnabled, ssrExperimentStore},
}: {
  routes: RouteObject[]
  options: SharedProviderOptions
}) {
  const sharedInternals = (
    <DataRouterExperimentScope>
      <Outlet />
      {children}
      <CommonElements ssrError={ssrError} />
      <SoftNavLifecycleListener />
      <NavigationFocusListener />
      <CombinedScrollRestoration />
      <PublishPayload />
      <TitleManager />
      <RouterDevTools routes={routes} />
    </DataRouterExperimentScope>
  )
  return (
    <BaseProviders appName={appName} dataRouterEnabled={dataRouterEnabled} ssrExperimentStore={ssrExperimentStore}>
      <AppPayloadContext value={appPayload}>
        {tanstackRouterEnabled ? (
          sharedInternals
        ) : (
          <RoutesContextProvider routes={routes}>{sharedInternals}</RoutesContextProvider>
        )}
      </AppPayloadContext>
    </BaseProviders>
  )
}

function DataRouterExperimentScope({children}: PropsWithChildren) {
  if (!isFeatureEnabled('client_experiment_assignment_forwarding')) return children
  return <EnabledDataRouterExperimentScope>{children}</EnabledDataRouterExperimentScope>
}

function EnabledDataRouterExperimentScope({children}: PropsWithChildren) {
  const renderedLocation = useRenderedLocation()
  const navigation = useNavigation()
  const destination = navigation.location ?? renderedLocation
  const destinationKey = `${destination.pathname}${destination.search}`
  const acceptedDestinationKey = `${renderedLocation.pathname}${renderedLocation.search}`
  const {scope} = useNavigationScopeTransaction({
    destinationKey,
    acceptedDestinationKey,
  })

  return <ExperimentScopeProvider scope={scope}>{children}</ExperimentScopeProvider>
}
